'use strict'

// A container death counts as a resource crash when the kernel OOM killer
// stopped it, or when Node aborted because the heap was exhausted.

function resourceCrashFromState (state) {

  if (!state) {

    return null
  }

  const finishedAt = state.FinishedAt || ''

  if (state.OOMKilled === true) {

    return { reason: 'oom', finishedAt, kernel: true }
  }

  const code = Number(state.ExitCode)

  if (code === 137) {

    return { reason: 'oom', finishedAt }
  }

  if (code === 134) {

    return { reason: 'heap', finishedAt }
  }

  return null
}

function shouldRecordCrash (account, crash) {

  if (!crash) {

    return false
  }

  const stamp = crash.finishedAt || ''

  if (!stamp) {

    return !account.resourceCrashAt
  }

  return account.resourceCrashFinishedAt !== stamp
}

function laterCrashAt (left, right) {

  if (!left) return right || ''
  if (!right) return left

  return left > right ? left : right
}

function crashTotal (account) {

  const count = Number(account && account.resourceCrashCount)

  if (Number.isFinite(count) && count > 0) return count
  if (account && account.resourceCrashAt) return 1

  return 0
}

const CRASH_LOG_LIMIT = 200

function crashReason (value) {

  if (value === 'oom' || value === 'heap' || value === 'update') return value

  return 'memory'
}

function crashReasonLabel (reason) {

  if (reason === 'oom') return 'The server ran out of memory'
  if (reason === 'heap') return 'The process ran out of memory'
  if (reason === 'update') return 'Code update'

  return 'Ran out of server resources'
}

function codeUpdateTotal (account) {

  const count = Number(account && account.codeUpdateCount)

  if (Number.isFinite(count) && count > 0) return count

  return 0
}

function crashEntry (entry) {

  if (!entry || typeof entry !== 'object') return null

  const at = entry.at ? String(entry.at) : ''

  if (!at) return null

  const count = Number(entry.count)

  return {
    at,
    count: Number.isFinite(count) && count > 0 ? Math.floor(count) : 1,
    reason: crashReason(entry.reason)
  }
}

function storedLog (account) {

  const list = account && Array.isArray(account.resourceCrashLog) ? account.resourceCrashLog : []

  return list.map(crashEntry).filter(Boolean).slice(-CRASH_LOG_LIMIT)
}

function seedLog (account) {

  const count = crashTotal(account)
  const at = account && account.resourceCrashAt ? String(account.resourceCrashAt) : ''

  if (!count || !at) return []

  return [{ at, count, reason: 'memory' }]
}

function crashLogForView (account) {

  const log = storedLog(account)

  if (log.length) return log

  return seedLog(account)
}

function eventsForDelta (events, delta) {

  const picked = []
  let left = delta

  for (let index = events.length - 1; index >= 0 && left > 0; index -= 1) {

    const event = events[index]

    if (event.count > left) break

    picked.unshift(event)
    left -= event.count
  }

  return { events: picked, remainder: left }
}

function nextCrashLog (account, report, delta) {

  const stored = storedLog(account)
  const log = stored.length ? stored.slice() : seedLog(account)

  if (delta <= 0) return log

  const incoming = (Array.isArray(report && report.events) ? report.events : []).map(crashEntry).filter((event) => event && event.reason !== 'update')
  const split = eventsForDelta(incoming, delta)
  const at = report && report.at ? String(report.at) : ''

  if (split.remainder > 0 && at) {

    log.push({ at, count: split.remainder, reason: crashReason(report && report.reason) })
  }

  log.push(...split.events)

  return log.slice(-CRASH_LOG_LIMIT)
}

function explainedLog (report, resourceCount, updateCount) {

  const events = (Array.isArray(report && report.events) ? report.events : []).map(crashEntry).filter(Boolean)

  if (!events.length) return null

  let resources = 0
  let updates = 0

  for (const event of events) {

    if (event.reason === 'update') updates += event.count
    else resources += event.count
  }

  if (resources !== resourceCount || updates !== updateCount) return null

  return events.slice(-CRASH_LOG_LIMIT)
}

function crashLogsMatch (account, log) {

  return JSON.stringify(storedLog(account)) === JSON.stringify(log || [])
}

function recordCrashEntry (account, entry) {

  const current = account || {}
  const normalized = crashEntry(entry)
  const added = normalized ? normalized.count : 0
  const log = storedLog(current)
  const next = log.length ? log.slice() : seedLog(current)

  if (normalized) next.push(normalized)

  return {
    resourceCrashAt: laterCrashAt(current.resourceCrashAt, normalized && normalized.at),
    resourceCrashCount: crashTotal(current) + added,
    resourceCrashLog: next.slice(-CRASH_LOG_LIMIT)
  }
}

function dismissCrashNotice (account) {

  const current = account || {}

  return {
    resourceCrashDismissedAt: current.resourceCrashAt || '',
    resourceCrashDismissedCount: crashTotal(current)
  }
}

function crashNoticeOpen (account) {

  const current = account || {}
  const count = crashTotal(current)

  if (count <= 0) return false

  const dismissedCount = Number(current.resourceCrashDismissedCount)

  return current.resourceCrashDismissedAt !== (current.resourceCrashAt || '') || dismissedCount !== count
}

// The file on the data volume counts process deaths. The account record also
// counts kernel OOM kills the file never saw. A repeated report of the same
// file count does not add those crashes again.
function applyCrashReport (account, report) {

  const current = account || {}
  const fileCount = Math.max(0, Number(report && report.count) || 0)
  let synced = Number(current.resourceCrashFileCount)

  if (!Number.isFinite(synced) || synced < 0) synced = 0

  const sameRecordedCrash = current.resourceCrashCount == null
    && current.resourceCrashFileCount == null
    && current.resourceCrashAt
    && report
    && report.at === current.resourceCrashAt
    && fileCount > 0

  if (sameRecordedCrash) synced = fileCount

  const delta = Math.max(0, fileCount - synced)
  const at = laterCrashAt(current.resourceCrashAt, report && report.at)
  const fileUpdates = Math.max(0, Number(report && report.updates) || 0)
  let syncedUpdates = Number(current.codeUpdateFileCount)

  if (!Number.isFinite(syncedUpdates) || syncedUpdates < 0) syncedUpdates = 0

  const updateDelta = Math.max(0, fileUpdates - syncedUpdates)
  const resourceCount = crashTotal(current) + delta
  const updateCount = codeUpdateTotal(current) + updateDelta
  const explained = explainedLog(report, resourceCount, updateCount)
  let log = explained

  if (!log) {

    log = nextCrashLog(current, report, delta)

    if (updateDelta > 0) {

      const incoming = (Array.isArray(report && report.events) ? report.events : []).map(crashEntry).filter((event) => event && event.reason === 'update')
      const split = eventsForDelta(incoming, updateDelta)
      const updateAt = report && report.updateAt ? String(report.updateAt) : ''

      if (split.remainder > 0 && updateAt) {

        log.push({ at: updateAt, count: split.remainder, reason: 'update' })
      }

      log.push(...split.events)
      log = log.slice(-CRASH_LOG_LIMIT)
    }
  }

  return {
    resourceCrashAt: at,
    resourceCrashCount: resourceCount,
    resourceCrashFileCount: Math.max(synced, fileCount),
    codeUpdateAt: laterCrashAt(current.codeUpdateAt, report && report.updateAt),
    codeUpdateCount: updateCount,
    codeUpdateFileCount: Math.max(syncedUpdates, fileUpdates),
    resourceCrashLog: log
  }
}

module.exports = {
  resourceCrashFromState,
  shouldRecordCrash,
  applyCrashReport,
  crashLogForView,
  crashLogsMatch,
  crashNoticeOpen,
  crashReasonLabel,
  codeUpdateTotal,
  dismissCrashNotice,
  recordCrashEntry
}
